import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import options from "./js/options.js"
import light from "./js/light.js"
import loadingManager from "./js/loader.js"
import { pdfCreate } from "./js/pdf.js"
import { EffectComposer, EXRLoader, RenderPass, SSAOPass, SMAAPass, OutputPass, ShaderPass } from "three/addons"
import theme from "./js/theme.js"
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js'
import { initAIImageGenerator } from "./js/ai.js"

// ─── СЦЕНА ───────────────────────────────────────────────────────────────────

const canvasContainer = document.querySelector('#canvas-container')
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
const isLarge = canvasContainer.getBoundingClientRect().width > 600

const sizes = {
    width:  canvasContainer.getBoundingClientRect().width,
    height: canvasContainer.getBoundingClientRect().height
}

// ─── ЗАГРУЗЧИКИ ──────────────────────────────────────────────────────────────

const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('./draco/')

const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

// ─── КАМЕРА ──────────────────────────────────────────────────────────────────

const camera = new THREE.PerspectiveCamera(38, sizes.width / sizes.height, 0.3, 100)
isLarge ? camera.position.set(5.948, 7.072, 4.964) : camera.position.set(6.675, 10.084, 12.543)
scene.add(camera)

const defaultCameraPos    = camera.position.clone()
const defaultCameraTarget = new THREE.Vector3(0, 0.75, 0)

// ─── КОНТРОЛЫ ────────────────────────────────────────────────────────────────

const controls = new OrbitControls(camera, canvas)
controls.target.copy(defaultCameraTarget)
controls.enableDamping  = true
controls.dampingFactor  = 0.04
controls.enablePan      = true
controls.minDistance    = 1
controls.maxDistance    = isLarge ? 10: 17
controls.maxPolarAngle = Math.PI / 2


// ─── SHIFT+CLICK — лог координат ─────────────────────────────────────────────

const _raycaster = new THREE.Raycaster()
const _mouse     = new THREE.Vector2()

canvas.addEventListener('click', (e) => {
    if (!e.shiftKey) return

    const rect = canvas.getBoundingClientRect()
    _mouse.x =  ((e.clientX - rect.left)  / rect.width)  * 2 - 1
    _mouse.y = -((e.clientY - rect.top)   / rect.height) * 2 + 1

    _raycaster.setFromCamera(_mouse, camera)
    const intersects = _raycaster.intersectObjects(scene.children, true)
    const hit = intersects.find(i => i.object.isMesh)

    const cp = camera.position
    const ct = controls.target

    if (hit) {
        const p = hit.point
        console.log(`cameraPos: new THREE.Vector3(${cp.x.toFixed(3)}, ${cp.y.toFixed(3)}, ${cp.z.toFixed(3)})`)
        console.log(`target:    new THREE.Vector3(${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)})`)
    } else {
        console.log(`cameraPos: new THREE.Vector3(${cp.x.toFixed(3)}, ${cp.y.toFixed(3)}, ${cp.z.toFixed(3)})`)
        console.log(`target:    new THREE.Vector3(${ct.x.toFixed(3)}, ${ct.y.toFixed(3)}, ${ct.z.toFixed(3)})`)
    }
})

// ─── РЕНДЕРЕР ────────────────────────────────────────────────────────────────

const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: false,
    preserveDrawingBuffer: true,
    alpha: false,
    powerPreference: 'high-performance'
})

renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.shadowMap.enabled  = true
renderer.shadowMap.type     = THREE.PCFSoftShadowMap
renderer.setClearColor('#f0ede8')
renderer.toneMapping        = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.8
renderer.outputColorSpace   = THREE.SRGBColorSpace

// ─── HDR ENVIRONMENT ─────────────────────────────────────────────────────────

const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()

new EXRLoader().load('./models/brown_photostudio_04_2k.exr', (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping
    const envMap = pmremGenerator.fromEquirectangular(texture).texture
    scene.environment          = envMap
    scene.environmentIntensity = 0.6
    texture.dispose()
    pmremGenerator.dispose()
})

// ─── ОСВЕЩЕНИЕ ───────────────────────────────────────────────────────────────

light(scene)

// Основной направленный свет (сверху-спереди-справа)
const keyLight = new THREE.DirectionalLight(0xfff8f0, 0.90)
keyLight.position.set(5, 9, 6)
keyLight.castShadow = true
keyLight.shadow.mapSize.width  = 4096
keyLight.shadow.mapSize.height = 4096
keyLight.shadow.camera.near   = 0.5
keyLight.shadow.camera.far    = 30
keyLight.shadow.camera.left   = -6
keyLight.shadow.camera.right  = 6
keyLight.shadow.camera.top    = 6
keyLight.shadow.camera.bottom = -6
keyLight.shadow.bias          = -0.0003
keyLight.shadow.normalBias    = 0.015
keyLight.shadow.radius        = 2
scene.add(keyLight)

// Контровый свет сзади-слева
const rimLight = new THREE.DirectionalLight(0xddeeff, 0.28)
rimLight.position.set(-6, 4, -3)
scene.add(rimLight)

// Заполняющий снизу-спереди — главный фикс тёмного днища
const fillLight = new THREE.DirectionalLight(0xfff5e8, 0.55)
fillLight.position.set(0, -6, 4)
scene.add(fillLight)

// Боковой fill снизу-слева — убирает тёмный левый борт
const fillLightLeft = new THREE.DirectionalLight(0xc8d8ff, 0.35)
fillLightLeft.position.set(-5, -4, 0)
scene.add(fillLightLeft)

// Боковой fill снизу-справа — убирает тёмный правый борт
const fillLightRight = new THREE.DirectionalLight(0xc8d8ff, 0.35)
fillLightRight.position.set(5, -4, 0)
scene.add(fillLightRight)

// Ambient — общий рассеянный свет
const ambientLight = new THREE.AmbientLight(0xfff0e8, 0.55)
scene.add(ambientLight)

// Плоскость тени
const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(20, 20),
    new THREE.ShadowMaterial({ opacity: 0.20 })
)
shadowPlane.rotation.x = -Math.PI / 2
shadowPlane.position.y = -0.001
shadowPlane.receiveShadow = true
scene.add(shadowPlane)

// ─── POST-PROCESSING ─────────────────────────────────────────────────────────

const pixelRatio = Math.min(window.devicePixelRatio, 2)

const renderTarget = new THREE.WebGLRenderTarget(
    sizes.width  * pixelRatio,
    sizes.height * pixelRatio,
    {
        samples:     8,
        type:        THREE.HalfFloatType,
        colorSpace:  THREE.SRGBColorSpace
    }
)

const composer = new EffectComposer(renderer, renderTarget)
composer.setPixelRatio(pixelRatio)
composer.setSize(sizes.width, sizes.height)
composer.addPass(new RenderPass(scene, camera))

const ssaoPass = new SSAOPass(scene, camera, sizes.width, sizes.height)
ssaoPass.kernelRadius  = 0.22
ssaoPass.minDistance   = 0.0008
ssaoPass.maxDistance   = 0.014
ssaoPass.output        = SSAOPass.OUTPUT.Default
composer.addPass(ssaoPass)

const smaaPass = new SMAAPass(sizes.width * pixelRatio, sizes.height * pixelRatio)
composer.addPass(smaaPass)

// Color Grade
const colorGradeShader = {
    uniforms: {
        tDiffuse:   { value: null },
        brightness: { value: 0.0  },
        contrast:   { value: 0.0  },
        saturation: { value: 0.75  },
        gamma:      { value: 1.0  }
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float brightness;
        uniform float contrast;
        uniform float saturation;
        uniform float gamma;
        varying vec2 vUv;
        void main() {
            vec4 texel = texture2D(tDiffuse, vUv);
            vec3 color = texel.rgb;
            color += brightness;
            color = (color - 0.5) * (1.0 + contrast) + 0.5;
            float lum = dot(color, vec3(0.2126, 0.7152, 0.0722));
            color = mix(vec3(lum), color, saturation);
            color = pow(max(color, 0.0), vec3(1.0 / gamma));
            gl_FragColor = vec4(color, texel.a);
        }
    `
}

const colorGradePass = new ShaderPass(colorGradeShader)
composer.addPass(colorGradePass)
composer.addPass(new OutputPass())

controls.addEventListener('start', () => { smaaPass.enabled = false })
controls.addEventListener('end',   () => { setTimeout(() => { smaaPass.enabled = true }, 150) })

// ─── ФОН ─────────────────────────────────────────────────────────────────────

const backgroundMaterial = new THREE.ShaderMaterial({
    depthTest:  true,
    depthWrite: false,
    uniforms: {
        centerColor: { value: new THREE.Color(0xf5f1eb) },
        edgeColor:   { value: new THREE.Color(0xd8d0c4) },
        uAspect:     { value: sizes.width / sizes.height }
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 0.9999, 1.0);
        }
    `,
    fragmentShader: `
        varying vec2 vUv;
        uniform vec3 centerColor;
        uniform vec3 edgeColor;
        uniform float uAspect;
        float random(vec2 c) {
            return fract(sin(dot(c, vec2(12.9898, 78.233))) * 43758.5453);
        }
        void main() {
            vec2 uv = vUv;
            uv.x = (vUv.x - 0.5) * uAspect + 0.5;
            float factor = smoothstep(0.1, 0.9, distance(uv, vec2(0.5, 0.45)));
            vec3 color = mix(centerColor, edgeColor, factor * 0.7);
            float noise = (random(vUv) - 0.5) * (1.5 / 255.0);
            gl_FragColor = vec4(color + noise, 1.0);
        }
    `
})

const backgroundMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backgroundMaterial)
backgroundMesh.frustumCulled = false
backgroundMesh.position.z = -50
scene.add(backgroundMesh)

theme(scene, backgroundMaterial)

// ─── ТОЧКИ КАМЕРЫ ────────────────────────────────────────────────────────────

const cameraPoints = [
    {
        label: '1',
        position:  new THREE.Vector3(0.034, 1.356,  0.830),
        cameraPos: new THREE.Vector3(-0.026, 2.572, -1.719),
    },
    {
        label: '2',
        position:  new THREE.Vector3(-0.647, 1.675,  1.800),
        cameraPos: new THREE.Vector3(-3.422, 5.012,  6.924),
    },
    {
        label: '3',
        position:  new THREE.Vector3( 0.662, 1.270, -1.023),
        cameraPos: new THREE.Vector3( 6.039, 5.064, -5.402),
    },
    {
        label: '4',
        position:  new THREE.Vector3(-0.077, 1.230, -1.167),
        cameraPos: new THREE.Vector3( 1.227, 4.849,  2.077),
    },
]

const pointStyle = `
    position: absolute;
    width: 32px; height: 32px;
    border-radius: 50%;
    background: rgba(255,255,255,0.92);
    border: 2px solid rgba(0,0,0,0.15);
    box-shadow: 0 2px 8px rgba(0,0,0,0.25);
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600; color: #222;
    cursor: pointer;
    transform: translate(-50%, -50%);
    transition: background 0.2s, transform 0.15s;
    user-select: none; z-index: 10;
`

canvasContainer.style.position = 'relative'

const pointEls = cameraPoints.map((pt) => {
    const el = document.createElement('div')
    el.style.cssText = pointStyle
    el.textContent  = pt.label
    el.title        = `Point ${pt.label}`
    el.addEventListener('mouseenter', () => { el.style.background = 'rgba(255,255,255,1)';    el.style.transform = 'translate(-50%, -50%) scale(1.15)' })
    el.addEventListener('mouseleave', () => { el.style.background = 'rgba(255,255,255,0.92)'; el.style.transform = 'translate(-50%, -50%) scale(1)' })
    el.addEventListener('click', () => animateCamera(pt.cameraPos, pt.position))
    canvasContainer.appendChild(el)
    return { el, worldPos: pt.position }
})

// ─── КНОПКИ УПРАВЛЕНИЯ ───────────────────────────────────────────────────────

const btnContainer = document.createElement('div')
btnContainer.style.cssText = `
    position: absolute; bottom: 20px; left: 50%;
    transform: translateX(-50%);
    display: flex; gap: 10px; z-index: 10;
`
canvasContainer.appendChild(btnContainer)

const btnStyle = `
    padding: 8px 20px;
    background: rgba(255,255,255,0.92);
    border: 2px solid rgba(0,0,0,0.12);
    border-radius: 20px;
    font-size: 14px; font-weight: 600; color: #222;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    transition: background 0.2s;
    white-space: nowrap;
`

const resetBtn = document.createElement('button')
resetBtn.textContent    = 'Reset view'
resetBtn.style.cssText  = btnStyle
resetBtn.addEventListener('mouseenter', () => { resetBtn.style.background = 'rgba(255,255,255,1)' })
resetBtn.addEventListener('mouseleave', () => { resetBtn.style.background = 'rgba(255,255,255,0.92)' })
resetBtn.addEventListener('click', () => animateCamera(defaultCameraPos, defaultCameraTarget))
btnContainer.appendChild(resetBtn)

let pointsVisible = true
const toggleBtn = document.createElement('button')
toggleBtn.textContent   = 'Hide points'
toggleBtn.style.cssText = btnStyle
toggleBtn.addEventListener('mouseenter', () => { toggleBtn.style.background = 'rgba(255,255,255,1)' })
toggleBtn.addEventListener('mouseleave', () => { toggleBtn.style.background = pointsVisible ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.15)' })
toggleBtn.addEventListener('click', () => {
    pointsVisible = !pointsVisible
    pointEls.forEach(({ el }) => { el.style.display = pointsVisible ? 'flex' : 'none' })
    toggleBtn.style.background = pointsVisible ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.15)'
    toggleBtn.style.color      = pointsVisible ? '#222' : '#fff'
    toggleBtn.textContent      = pointsVisible ? 'Hide points' : 'Show points'
})
btnContainer.appendChild(toggleBtn)

// ─── АНИМАЦИЯ КАМЕРЫ ─────────────────────────────────────────────────────────

function animateCamera(targetPos, targetLookAt) {
    const startPos    = camera.position.clone()
    const startTarget = controls.target.clone()
    const duration    = 900
    const startTime   = performance.now()

    function animStep() {
        const t     = Math.min((performance.now() - startTime) / duration, 1)
        const eased = t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2
        camera.position.lerpVectors(startPos, targetPos, eased)
        controls.target.lerpVectors(startTarget, targetLookAt, eased)
        controls.update()
        if (t < 1) requestAnimationFrame(animStep)
    }
    animStep()
}

// ─── ПОЗИЦИИ ТОЧЕК НА ЭКРАНЕ ─────────────────────────────────────────────────

const _vec = new THREE.Vector3()

function updatePointPositions() {
    pointEls.forEach(({ el, worldPos }) => {
        if (!pointsVisible) return
        _vec.copy(worldPos).project(camera)
        if (_vec.z > 1) { el.style.display = 'none'; return }
        el.style.display = 'flex'
        el.style.left = `${( _vec.x * 0.5 + 0.5) * sizes.width}px`
        el.style.top  = `${(-_vec.y * 0.5 + 0.5) * sizes.height}px`
    })
}

// ─── МОДЕЛЬ ──────────────────────────────────────────────────────────────────

let model          = null
let originalMaterial = null

gltfLoader.load('./models/motorboat.glb', async (gltf) => {
    model = gltf.scene

    model.traverse((child) => {
        if (!child.isMesh) return

        child.castShadow    = child.visible
        child.receiveShadow = true

        const mat  = child.material
        if (!mat) return

        const maps = [mat.map, mat.normalMap, mat.roughnessMap, mat.metalnessMap, mat.aoMap, mat.emissiveMap]
        maps.forEach(tex => {
            if (!tex) return
            tex.anisotropy  = renderer.capabilities.getMaxAnisotropy()
            tex.minFilter   = THREE.LinearMipmapLinearFilter
            tex.needsUpdate = true
        })
        mat.needsUpdate = true
    })

    model.position.y = 0.3
    scene.add(model)

    options(model, originalMaterial, scene, camera, gui)
    await pdfCreate(renderer, camera, scene, model, backgroundMaterial)
    initAIImageGenerator({ renderer, camera, scene })
})

// ─── RESIZE ──────────────────────────────────────────────────────────────────

window.addEventListener('resize', () => {
    sizes.width  = canvasContainer.clientWidth
    sizes.height = canvasContainer.clientHeight

    const pr = Math.min(window.devicePixelRatio, 2)

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(pr)

    composer.setSize(sizes.width, sizes.height)
    composer.setPixelRatio(pr)

    renderTarget.setSize(sizes.width * pr, sizes.height * pr)
    smaaPass.setSize(sizes.width * pr, sizes.height * pr)
    ssaoPass.setSize(sizes.width, sizes.height)

    backgroundMaterial.uniforms.uAspect.value = sizes.width / sizes.height
})

// ─── GUI ─────────────────────────────────────────────────────────────────────

const gui = new GUI()
gui.hide()

const renderFolder = gui.addFolder('Рендерер')
renderFolder.add(renderer, 'toneMappingExposure', 0.3, 1.5, 0.01).name('Экспозиция')
renderFolder.add({ toneMapping: 'ACESFilmic' }, 'toneMapping', ['Reinhard', 'ACESFilmic', 'Linear'])
    .name('Tone Mapping')
    .onChange(v => {
        renderer.toneMapping = v === 'Reinhard' ? THREE.ReinhardToneMapping
            : v === 'ACESFilmic' ? THREE.ACESFilmicToneMapping
                : THREE.LinearToneMapping
    })

const gradeFolder = gui.addFolder('Color Grade')
gradeFolder.add(colorGradePass.uniforms.brightness, 'value', -0.5, 0.5, 0.01).name('Яркость')
gradeFolder.add(colorGradePass.uniforms.contrast,   'value', -1.0, 1.0, 0.01).name('Контраст')
gradeFolder.add(colorGradePass.uniforms.saturation, 'value',  0.0, 2.0, 0.01).name('Насыщенность')
gradeFolder.add(colorGradePass.uniforms.gamma,      'value',  0.5, 2.0, 0.01).name('Гамма')
gradeFolder.open()

const ssaoFolder = gui.addFolder('SSAO')
ssaoFolder.add(ssaoPass, 'enabled')
ssaoFolder.add(ssaoPass, 'kernelRadius', 0,      1,    0.01  ).name('Радиус')
ssaoFolder.add(ssaoPass, 'minDistance',  0,      0.05, 0.0001).name('Мин. дист')
ssaoFolder.add(ssaoPass, 'maxDistance',  0,      0.1,  0.001 ).name('Макс. дист')

const aaFolder = gui.addFolder('Антиалиасинг')
aaFolder.add(smaaPass, 'enabled').name('SMAA')

const lightFolder = gui.addFolder('Освещение')
lightFolder.add(keyLight,       'intensity', 0, 3,   0.05).name('Key')
lightFolder.add(rimLight,       'intensity', 0, 1.5, 0.05).name('Rim')
lightFolder.add(fillLight,      'intensity', 0, 2,   0.05).name('Fill (низ)')
lightFolder.add(fillLightLeft,  'intensity', 0, 1.5, 0.05).name('Fill Left (низ)')
lightFolder.add(fillLightRight, 'intensity', 0, 1.5, 0.05).name('Fill Right (низ)')
lightFolder.add(ambientLight,   'intensity', 0, 1.5, 0.01).name('Ambient')
lightFolder.add({ env: 0.6 },  'env',       0, 2,   0.05).name('HDR').onChange(v => scene.environmentIntensity = v)

// ─── ЦИКЛ РЕНДЕРА ────────────────────────────────────────────────────────────

const tick = () => {
    controls.update()
    updatePointPositions()
    composer.render()
    window.requestAnimationFrame(tick)
}

tick()